import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { App } from "antd";
import { useCallback } from "react";
import { Menu } from "@devowl-wp/react-folder-tree";
import { applyToAttachmentsBrowser } from "../../../backbone/attachmentSortable.js";
import { useAppTreeControllerRef } from "../../../contexts/appTreeControllerContext.js";
import { isDarkMode } from "../../../utils/darkMode.js";
import { __, _i } from "../../../utils/i18n.js";
import { rmlOpts } from "../../../utils/rmlOpts.js";
const createSortableItems = (sortables, select, selectText, keyPrefix)=>Object.keys(sortables).map((sortKey)=>({
            key: keyPrefix ? `${keyPrefix}:${sortKey}` : sortKey,
            label: /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    sortables[sortKey],
                    " ",
                    select === sortKey && /*#__PURE__*/ _jsxs("strong", {
                        children: [
                            "(",
                            selectText,
                            ")"
                        ]
                    })
                ]
            })
        }));
function buildOrderMenuItems(controller) {
    const { selected, sortables } = controller.props.store;
    const isSortable = selected && selected.properties && selected.contentCustomOrder !== 2;
    if (!isSortable) {
        return [];
    }
    const items = [];
    if (!selected.orderAutomatically) {
        items.push({
            key: "dnd",
            label: __("Order content by drag & drop")
        });
    }
    if (selected.contentCustomOrder !== 0) {
        items.push({
            key: "reset",
            label: __("Reset order")
        });
    }
    if (!selected.orderAutomatically) {
        items.push({
            key: "applyOnce",
            label: __("Apply order once..."),
            disabled: selected.orderAutomatically,
            children: sortables ? createSortableItems(sortables, selected.lastOrderBy, __("Last"), "applyOnce") : undefined
        });
    }
    if (selected.orderAutomatically) {
        items.push({
            key: "resetAutomatically",
            label: __("Deactivate automatic ordering"),
            disabled: selected.contentCustomOrder === 0
        });
    }
    items.push({
        key: "applyAutomatically",
        label: __("Apply automatic order..."),
        children: sortables ? createSortableItems(sortables, selected.orderAutomatically && selected.lastOrderBy, __("Latest"), "applyAutomatically") : undefined
    });
    if (selected.contentCustomOrder === 1) {
        items.push({
            type: "divider"
        });
        items.push({
            key: "applyReindex",
            label: __("Reindex order")
        });
        items.push({
            key: "applyResetLast",
            label: __("Reset to last order")
        });
    }
    return items;
}
function useRenderOrderMenu() {
    const { message } = App.useApp();
    const controllerRef = useAppTreeControllerRef();
    const handleClick = useCallback(async ({ key, keyPath })=>{
        const controller = controllerRef.current;
        const path = [
            ...keyPath
        ].reverse();
        const orderKey = key.includes(":") ? key.split(":").pop() : key;
        const { selected } = controller.props.store;
        if (!selected) {
            return;
        }
        const applyOrder = async (folder, sortKey, automatically)=>{
            if (!rmlOpts.others.isPro && rmlOpts.others.showProHints) {
                controller.setState({
                    showProFeature: "order-content"
                });
            } else {
                const hide = message.loading(_i(__("Order content by {{strong}}%s{{/strong}}...", folder.title), {
                    strong: /*#__PURE__*/ _jsx("strong", {})
                }));
                const { attachmentsBrowser } = controller;
                await folder.applyOrder(sortKey, automatically);
                applyToAttachmentsBrowser(attachmentsBrowser, folder);
                controller.handleReload();
                hide();
            }
        };
        if (key === "dnd") {
            controller.handleOrderClick();
        } else if (key.startsWith("applyOnce:") || path.includes("applyOnce")) {
            await applyOrder(selected, orderKey);
        } else if (key.startsWith("applyAutomatically:") || path.includes("applyAutomatically")) {
            await applyOrder(selected, orderKey, true);
        } else if (key === "reset") {
            await applyOrder(selected, "original");
        } else if (key === "resetAutomatically") {
            await applyOrder(selected, "deactivate");
        } else if (key === "applyReindex") {
            await applyOrder(selected, "reindex");
        } else if (key === "applyResetLast") {
            await applyOrder(selected, "last");
        }
    }, [
        controllerRef,
        message
    ]);
    return useCallback(()=>{
        const items = buildOrderMenuItems(controllerRef.current);
        return /*#__PURE__*/ _jsx(Menu, {
            items: items.length ? items : undefined,
            onClick: handleClick,
            style: items.length ? undefined : {
                visibility: "hidden"
            },
            theme: isDarkMode() ? "dark" : "light"
        });
    }, [
        controllerRef,
        handleClick
    ]);
}
export { useRenderOrderMenu };
